// src/pages/PostDetail/index.jsx
import React, { useState } from 'react';
import { Layout, Typography, Tag, Space, Divider, Spin, Button, message } from 'antd';
import { 
  EllipsisOutlined,
  CalendarOutlined, 
  UserOutlined, 
  EyeOutlined, 
  MessageOutlined, 
  EditOutlined, 
  DeleteOutlined,
  LikeOutlined,
  ShareAltOutlined
} from '@ant-design/icons';
import { Link, useParams, useNavigate } from 'react-router-dom';
import AppHeader from '../../components/Header';
import AppFooter from '../../components/Footer';
import { useBlog } from '../../context/BlogContext';
import './index.css';

const { Title, Text, Paragraph } = Typography;

const PostDetail = ({ loggedInUser, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { posts, deletePost } = useBlog();
  const [loading, setLoading] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  // 查找文章
  const post = posts.find(p => p.id === parseInt(id));

  // 如果文章不存在，显示404
  if (!post && !loading) {
    return (
      <Layout className="blog-layout">
        <AppHeader loggedInUser={loggedInUser} onLogout={onLogout} />
        <div className="blog-content">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '60vh' 
          }}>
            <div>
              <Title level={2}>文章未找到</Title>
              <Link to="/">
                <Button type="primary">返回首页</Button>
              </Link>
            </div>
          </div>
        </div>
        <AppFooter />
      </Layout>
    );
  }

  const isAuthor = loggedInUser && loggedInUser.id === post?.author?.id;

  const handleDelete = () => {
    setLoading(true);
    message.loading('正在删除文章...');
    
    setTimeout(() => {
      try {
        deletePost(parseInt(id));
        message.success('文章删除成功');
        navigate('/');
      } catch (error) {
        message.error('删除失败');
      } finally {
        setLoading(false);
      }
    }, 1000);
  };

  const handleLike = () => {
    if (!liked) {
      setLikeCount(prev => prev + 1);
      setLiked(true);
    } else {
      setLikeCount(prev => prev - 1);
      setLiked(false);
    }
  };

  if (loading) {
    return (
      <Layout className="blog-layout">
        <AppHeader loggedInUser={loggedInUser} onLogout={onLogout} />
        <div className="post-detail-loading">
          <Spin size="large" tip="加载中..." />
        </div>
        <AppFooter />
      </Layout>
    );
  }

  return (
    <Layout className="blog-layout">
      <AppHeader loggedInUser={loggedInUser} onLogout={onLogout} />
      <div className="blog-content post-detail-content">
        <div className="post-detail-container">
          <article className="post-detail-article">
            <div className="post-detail-header">
              <Title level={2} className="post-detail-title">
                {post?.title}
              </Title>
              
              <div className="post-detail-meta">
                <Space size="large">
                  <Text className="post-detail-author">
                    <UserOutlined /> {post?.author?.name || post?.author?.username}
                  </Text>
                  <Text className="post-detail-date">
                    <CalendarOutlined /> {new Date(post?.createdAt).toLocaleDateString()}
                  </Text>
                  <Text className="post-detail-views">
                    <EyeOutlined /> {post?.views || 0} 次浏览
                  </Text>
                  <Text className="post-detail-comments">
                    <MessageOutlined /> {post?.comments || 0} 条评论
                  </Text>
                </Space>
                
                <div className="post-detail-tags" style={{ marginTop: '16px' }}>
                  <Tag color="blue">{post?.category}</Tag>
                  {post?.tags?.map((tag, index) => (
                    <Tag key={index} color="default">{tag}</Tag>
                  ))}
                </div>
              </div>
              
              {isAuthor && (
                <div className="post-detail-actions">
                  <Link to={`/edit/${post?.id}`}>
                    <Button icon={<EditOutlined />} type="primary">
                      编辑文章
                    </Button>
                  </Link>
                  <Button 
                    icon={<DeleteOutlined />} 
                    danger 
                    onClick={handleDelete}
                    style={{ marginLeft: '10px' }}
                  >
                    删除文章
                  </Button>
                </div>
              )}
            </div>
            
            <Divider />
            
            <div 
              className="post-detail-content"
              dangerouslySetInnerHTML={{ __html: post?.content }}
            />
            
            <div className="post-detail-footer">
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginTop: '30px',
                paddingTop: '20px',
                borderTop: '1px solid #f0f0f0'
              }}>
                <Button 
                  icon={<LikeOutlined />} 
                  onClick={handleLike}
                  type={liked ? "primary" : "default"}
                >
                  赞 ({likeCount})
                </Button>
                
                <div>
                  <Button icon={<EllipsisOutlined />}>评论</Button>
                  <Button icon={<ShareAltOutlined />}>分享</Button>
                 
            
                </div>
              </div>
            </div>
          </article>
          
          <div className="post-detail-sidebar">
            <div className="post-author-card">
              <Title level={4}>作者</Title>
              <div className="author-info">
                <div className="author-avatar">
                  <UserOutlined style={{ fontSize: '32px' }} />
                </div>
                <div className="author-details">
                  <Text strong>{post?.author?.name || post?.author?.username}</Text>
                  <Text type="secondary">全栈开发者</Text>
                  <Text type="secondary">文章: 10篇</Text>
                </div>
              </div>
              <Button block style={{ marginTop: '15px' }}>关注作者</Button>
              <Button block style={{ marginTop: '15px' }}>作者作品</Button>
            </div>
          
            <div className="related-posts">
              <Title level={4}>相关文章</Title>
              <ul className="related-posts-list">
                <li>
                  <Link to="/post/2">数据库详解</Link>
                </li>
                <li>
                  <Link to="/post/3">人工智能：未来已来</Link>
                </li>
                <li>
                  <Link to="/post/4">前端开发的趋势</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <AppFooter />
    </Layout>
  );
};

export default PostDetail;